import browserHistory from '../history';
import _ from 'lodash';
import UserManager from './UserManager';
import queryString from 'query-string';
import { matchPath } from 'react-router';
import GlobalConst from '../utils/GlobalConst';
import PubSub from 'pubsub-js';
import RoutePaths from '../utils/RoutePaths';

export default {

	goBack() {
		browserHistory.goBack();
	},

	goBackWithPath(path=null) {
		if (document.referrer!='') this.goTo(path);
		else this.goBack();
	},

	currentQuery() {
		return queryString.parse(browserHistory.location.search);
	},

	getQueryKeyValue(key, url = window.location.href) {
		const name = key.replace(/[\[\]]/g, '\\$&');
		var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
			results = regex.exec(url);
		if (!results) return null;
		if (!results[2]) return '';
		return decodeURIComponent(results[2]);
	},

	// `search`, not `query`: history v4 ignores a `query` key, so the remaining params would be
	// dropped along with `keys`. Absent keys return early rather than replacing with an unchanged
	// location, which would otherwise touch history on every page that never carried them.
	removeFromQuery(keys) {
		const currentQuery = this.currentQuery();
		const presentKeys = _.intersection(_.castArray(keys), _.keys(currentQuery));
		if (_.isEmpty(presentKeys)) return;
		const remaining = queryString.stringify(_.omit(currentQuery, presentKeys));
		browserHistory.replace({
			pathname: this.currentPath(),
			search: remaining ? `?${remaining}` : ''
		});
	},

	currentPath() {
		return browserHistory.location.pathname;
	},

	currentHost() {
		return browserHistory.location.host;
	},

	currentOrigin() {
		return window.location.origin;
	},

	currentPathMatches(path) {
		const match = matchPath(this.currentPath(), {path});
		return !_.isNil(match);
	},

	goTo(path, query = null) {
		if(!query) {
			browserHistory.push(path);
		} else {
			browserHistory.push({pathname: path, search: queryString.stringify(query)});
		}
		PubSub.publish(GlobalConst.SET_BREADCRUMBS_MSG)
	},

	currentLocationState() {
		return browserHistory.location.state || {};
	},

	goToExternal(path, newWindow=false) {
		if (!newWindow) {
			window.open(path);
		} else {
			window.open(path, '_blank');
		}
	},

	// Carries the entry's state over, since rewriting the query would otherwise drop it.
	replaceQuery(newQuery) {
		browserHistory.replace({
			pathname: this.currentPath(),
			search: queryString.stringify(newQuery),
			state: browserHistory.location.state
		})
	},

	replaceRawQuery(newQuery) {
		browserHistory.replace({
			pathname: this.currentPath(),
			search: newQuery
		})
	},

	goToBrandRegister() {
		this.goTo('/brand_register');
	},

	goToCreatorRegister() {
		this.goTo('/creator_register');
	},

	goToUserCabinet() {
		this.goTo('/my_profile');
	},

	goToWelcomePage() {
		this.goTo('/welcome');
	},

	goToErrorPage() {
		this.goTo('/maintenance');
	},

	goToUserList() {
		this.goTo('/users');
	},

	goToLogin(checkFrom = false, error = null) {
		const query = {};
		if(checkFrom){
			query.from = location.pathname + location.search;
		}
		if(error){
			query.error= error;
		}
		this.goTo('/login', query);
	},

	goToDefaultRoute() {
		if (UserManager.user.isActive) return this.goTo(this.takeHome());
		else if (!UserManager.user.isActive && UserManager.user.email) return this.goToWelcomePage();
		else if (!UserManager.isAuthorized) return this.goToLogin();
	},

	takeHome() {
		return '/home';
	},

	internalChannelLink(id) {
		const path = this.currentPath();
		if(/explore\/channels$/i.test(path)) {
			return `${path}/${id}`;
		}
		if(/my_profile\/channels$/i.test(path)) {
			return `${path}/${id}`;
		}
		if(/explore\/videos\/(?!.*\/).*/i.test(path)) {
			return `${RoutePaths.EXPLORE.path}/channels/${id}`;
		}
		if(/explore\/videos$/i.test(path)) {
			return `${RoutePaths.EXPLORE.path}/channels/${id}`;
		}
		return `${RoutePaths.EXPLORE.path}/channels/${id}`;
	},

	toLegacyBuild() {
		return `/legacy_build`;
	},

	toAdvancedBuild() {
		return `/advanced_build`;
	},

	vettingToolLink(id) {
		return `/legacy_build/vetting_tool/${id}`;
	},

	vettingToolLinkResearch(id, type, toResearch=false) {
		if (toResearch) return `${RoutePaths.EXPLORE.path}/${type}s`;
		return `${RoutePaths.EXPLORE.path}/${type}s/vetting_tool/${id}`;
	},

	internalVideoLink(id) {
		const path = this.currentPath();
		if(/explore\/videos$/i.test(path)) {
			return `${path}/${id}`;
		}
		if(/explore\/channels/i.test(path)) {
			return `${RoutePaths.EXPLORE.path}/videos/${id}`;
		}
		if(/explore\/keywords/i.test(path)) {
			return `${RoutePaths.EXPLORE.path}/videos/${id}`;
		}
		if(/my_profile\/channels/i.test(path)) {
			return `/my_profile/videos/${id}`;
		}
		return `${RoutePaths.EXPLORE.path}/videos/${id}`;
	},

	// The id is a media URL, so it travels in the query string: as a path segment its dots make
	// static-file handlers claim the request before the SPA ever loads.
	ctvEpisodeLink(id) {
		return `${RoutePaths.EXPLORE.path}/ctv/episodes/detail?id=${encodeURIComponent(id)}`;
	},

	ctvShowLink(id) {
		return `${RoutePaths.EXPLORE.path}/ctv/shows/detail?id=${encodeURIComponent(id)}`;
	},

	redditSubredditLink(id) {
		return `${RoutePaths.EXPLORE.path}/reddit/subreddits/detail?id=${encodeURIComponent(id)}`;
	},

	internalKeywordLink(id) {
		const path = this.currentPath();
		if(/explore\/keywords$/i.test(path)) {
			return `${path}/${id}`;
		}
		return `${RoutePaths.EXPLORE.path}/keywords/${id}`;
	},

	internalSegmentLink(type, id) {
		return `/media_planning/custom_segments/${type}/${id}`;
	},

	internalAWAccountLink(id) {
		const path = this.currentPath();
		if(/reporting$/i.test(path)) {
			return `${path}/${id}`;
		}
		if(/reporting\/(?!.*\/).*/i.test(path)) {
			return path;
		}
		if(/accounts$/i.test(path)) {
			return `${path}/${id}/`;
		}
		return '/reporting/';
	},

	internalAWAccountSetupLink(accountID, campaignID = null) {
		let res = `/analytics/self_service/${accountID}/setup`;
		if(campaignID) {
			res += `?campaignID=${campaignID}`;
		}
		return res;
	},

	internalUserPermissionsPageLink(userID){
		return `/users/permissions/${userID}/`;
	},

	internalRolePermissionsPageLink(roleID){
		return `/roles/permissions/${roleID}/`;
	},

	internalPacingIQLink(type, opportunityID, placementID) {
		if (type=='placement') return `/pacing_iq/${type}s/${opportunityID}/`;
		else if (type=='campaign') return `/pacing_iq/${type}s/${opportunityID}/${placementID}/`;
	},

	accessDeniedPageURL() {
		return '/access_denied';
	},

	setTitle(title) {
		document.title = title ? `${title} | ViewIQ` : 'ViewIQ';
	}

};

